import React, { useEffect, useState } from 'react';
import qs from 'qs';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Hidden from '@material-ui/core/Hidden';
import Grid from '@material-ui/core/Grid';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import {
  submitSubscriptionResult,
  updateSubscriptionStatus,
} from 'containers/Auth/actions';
import {
  makeSelectProfile,
  makeSelectSubscriptionResult,
} from 'containers/Auth/selectors';
import { showTermsOfUse, showPrivacyPolicy } from 'containers/Main/actions';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import AppContainer from 'components/AppContainer';
import RedirectButton from 'components/Authentication/RedirectButton';
import AuthenticationImage from 'components/Authentication/AuthenticationImage';
import SubscriptionOptions from 'components/SubscriptionOptions';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { fallbackImage, getUserFirstName } from 'utils/stringUtils';

const useStyles = makeStyles(theme => ({
  formContainer: {
    paddingLeft: '3%',
    paddingRight: '3%',
  },
  linksContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    width: '100%',
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 24,
  },
  form: {
    paddingTop: 40,
    [theme.breakpoints.up('md')]: {
      maxWidth: 440,
    },
  },
  userContainer: {
    paddingTop: 24,
    paddingBottom: 30,
  },
  userWrapper: {
    border: '1px solid #E9ECED',
    borderRadius: 3,
    padding: '18px 24px',
  },
  userImage: {
    width: 63,
    height: 63,
    borderRadius: 2,
    objectFit: 'cover',
  },
  userInfoWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  labelCheck: {
    color: theme.palette.text.primary,
    ...theme.typography.overline,
  },
  marginNone: {
    margin: 0,
    padding: '24px 0 40px 0',
    alignItems: 'flex-start',
  },
  checkboxApply: {
    padding: '4px 14px 0 0',
  },
  checkboxIcon: {
    height: 17,
    width: 17,
  },
  caption: {
    color: theme.palette.text.primary,
    ...theme.typography.body2,
  },
  captionLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:focus': {
      outlineWidth: 0,
    },
    ...theme.typography.body2,
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    padding: 12,
    marginTop: 8,
    marginBottom: 5,
    textTransform: 'uppercase',
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
}));

const SubscriptionForm = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const profile = useSelector(makeSelectProfile());
  const clientDetails = useSelector(makeSelectClientDetails());
  const [accountSiteCopy, signInSiteCopy, signUpSiteCopy] = useSiteCopySelector(
    ['account', 'sign-in', 'sign-up'],
  );
  const [subscription, setSubscription] = useState(
    _get(accountSiteCopy, 'pageCopy.subscription', [])
      .filter(item => item.default)
      .map(item => item.value),
  );
  const classes = useStyles();
  const subscriptionResult = useSelector(makeSelectSubscriptionResult());

  const nextLocation = `${_isEmpty(redirect) ? '/' : redirect}`;

  useEffect(() => {
    if (
      !_isEmpty(subscriptionResult) &&
      _get(subscriptionResult, 'error') === false
    ) {
      history.push(nextLocation);

      return () => {
        dispatch(submitSubscriptionResult({}));
      };
    }
    return () => {};
  }, [dispatch, subscriptionResult, history, nextLocation]);

  const { redirect } = qs.parse(location.search.slice(1));

  let method;
  if (_get(profile, 'providerData.providerId', '').includes('microsoft.com'))
    method = 'Microsoft';
  else if (_get(profile, 'providerData.providerId', '').includes('google'))
    method = 'Google';
  else if (_get(profile, 'providerData.providerId', '').includes('facebook'))
    method = 'Facebook';
  else if (_get(profile, 'providerData.providerId', '').startsWith('saml')) {
    method = _get(clientDetails, 'samlConfiguration.providerName');
  }

  const firstName = getUserFirstName(profile);

  const handleCaptionClick = action => {
    if (action === 'agreement') dispatch(showTermsOfUse(true));
    else if (action === 'privacy') dispatch(showPrivacyPolicy(true));
  };

  const onSubscriptionChange = value => {
    if (subscription.includes(value)) {
      setSubscription(subscription.filter(item => item !== value));
    } else {
      setSubscription(subscription.concat(value));
    }
  };

  const handleSubmit = () => {
    if (subscription.length > 0) {
      dispatch(
        updateSubscriptionStatus({
          email: profile.email,
          subscription,
        }),
      );
    } else {
      history.push(nextLocation);
    }
  };

  return (
    <Grid container direction="row">
      <Hidden smDown>
        <AuthenticationImage />
      </Hidden>
      <Grid item xs={12} md={7} className={classes.formContainer}>
        <AppContainer direction="row">
          <Grid container justifyContent="center">
            <Grid item xs={12} className={classes.linksContainer}>
              <RedirectButton
                label={_get(
                  signInSiteCopy,
                  'pageCopy.subscriptionStepHomepageNavigation',
                )}
                url={redirect}
                color
              />
            </Grid>
            <Grid item xs={12} md={8} className={classes.form}>
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="h4" color="primary">
                    {_get(
                      signInSiteCopy,
                      'pageCopy.subscriptionStepHeader',
                      '',
                    ).replace('<method>', method)}
                  </Typography>
                </Grid>
                <Grid item xs={12} className={classes.userContainer}>
                  <div className={classes.userWrapper}>
                    <Grid container spacing={2}>
                      {profile.avatarUrl && (
                        <Grid item>
                          <img
                            src={profile.avatarUrl}
                            onError={e => {
                              e.target.src = fallbackImage;
                            }}
                            className={classes.userImage}
                            alt=""
                          />
                        </Grid>
                      )}
                      <Grid item xs className={classes.userInfoWrapper}>
                        {firstName && (
                          <Typography color="primary" variant="h6">
                            {firstName}
                          </Typography>
                        )}
                        <Typography color="textPrimary" variant="body2">
                          {profile.email}
                        </Typography>
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
                <Grid item xs={12}>
                  {_get(signUpSiteCopy, 'pageCopy.userTerms', []).map(el => {
                    if (el.action) {
                      return (
                        <span
                          key={el.value}
                          role="link"
                          tabIndex={0}
                          onKeyPress={() => handleCaptionClick(el.action)}
                          className={classes.captionLink}
                          onClick={() => handleCaptionClick(el.action)}
                        >
                          {el.value}
                        </span>
                      );
                    }

                    return (
                      <span key={el.value} className={classes.caption}>
                        {el.value}
                      </span>
                    );
                  })}
                </Grid>
                <Grid item xs={12}>
                  <SubscriptionOptions
                    subscription={subscription}
                    onChange={onSubscriptionChange}
                    isSmall
                  />
                </Grid>
                <Grid item xs={12}>
                  <ButtonBase className={classes.button} onClick={handleSubmit}>
                    {_get(
                      signInSiteCopy,
                      'pageCopy.subscriptionStepButton',
                      '',
                    )}
                  </ButtonBase>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AppContainer>
      </Grid>
    </Grid>
  );
};

export default SubscriptionForm;
